import { eventSourceForInboundSlug } from "./inboundWebhookSlug.js";

export const INBOUND_RECEIPT_DEFAULT_LIMIT = 50;
export const INBOUND_RECEIPT_MAX_LIMIT = 100;
export const INBOUND_RECEIPT_SEEN_SCAN = 300;

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PROCESSING = new Set(["processed", "skipped_duplicate", "error"]);

export type InboundReceiptFilters = {
  organizationId: string;
  source?: string;
  processingStatus?: string;
  unlinkedOnly: boolean;
  since?: string;
  until?: string;
  limit: number;
};

export type InboundReceiptRow = {
  id: string;
  organization_id: string;
  source: string;
  event_type: string;
  processing_status: string;
  created_at: string;
  deal_id: string | null;
  intake_id: string | null;
  error_message: string | null;
  payload_hash: string;
};

export type InboundReceiptDetail = InboundReceiptRow & { payload: unknown };

export type ReceiptSourceOption = { value: string; label: string };

export function parseInboundReceiptListInput(
  body: Record<string, unknown>
): { ok: true; filters: InboundReceiptFilters } | { ok: false; error: string } {
  const organizationId = typeof body.organization_id === "string" ? body.organization_id.trim() : "";
  if (!UUID.test(organizationId)) return { ok: false, error: "organization_id (uuid) required." };

  const sourceRaw = typeof body.source === "string" ? body.source.trim() : "";
  const source = sourceRaw ? eventSourceForInboundSlug(sourceRaw) : undefined;

  const statusRaw = typeof body.processing_status === "string" ? body.processing_status.trim() : "";
  const processingStatus = statusRaw && PROCESSING.has(statusRaw) ? statusRaw : undefined;

  const limitRaw = Number(body.limit ?? INBOUND_RECEIPT_DEFAULT_LIMIT);
  const limit = Number.isFinite(limitRaw)
    ? Math.max(1, Math.min(INBOUND_RECEIPT_MAX_LIMIT, Math.floor(limitRaw)))
    : INBOUND_RECEIPT_DEFAULT_LIMIT;

  const since = typeof body.since === "string" && body.since.trim() ? body.since.trim() : undefined;
  const until = typeof body.until === "string" && body.until.trim() ? body.until.trim() : undefined;

  return {
    ok: true,
    filters: {
      organizationId,
      source,
      processingStatus,
      unlinkedOnly: body.unlinked_only === true,
      since,
      until,
      limit,
    },
  };
}

export function mapInboundReceiptRow(raw: unknown, includePayload: boolean): InboundReceiptRow | InboundReceiptDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== "string" || typeof o.source !== "string") return null;
  const row: InboundReceiptRow = {
    id: o.id,
    organization_id: typeof o.organization_id === "string" ? o.organization_id : "",
    source: o.source,
    event_type: typeof o.event_type === "string" ? o.event_type : "unknown",
    processing_status: typeof o.processing_status === "string" ? o.processing_status : "",
    created_at: typeof o.created_at === "string" ? o.created_at : "",
    deal_id: typeof o.deal_id === "string" ? o.deal_id : null,
    intake_id: typeof o.intake_id === "string" ? o.intake_id : null,
    error_message: typeof o.error_message === "string" ? o.error_message : null,
    payload_hash: typeof o.payload_hash === "string" ? o.payload_hash : "",
  };
  if (!includePayload) return row;
  return { ...row, payload: o.payload ?? null };
}

export function mergeReceiptSourceOptions(
  named: Array<{ slug: string; display_name: string }>,
  seen: string[]
): ReceiptSourceOption[] {
  const byValue = new Map<string, string>();
  for (const n of named) {
    const value = eventSourceForInboundSlug(n.slug);
    if (!value) continue;
    const label = n.display_name.trim() || value;
    byValue.set(value, label === value ? value : `${label} (${value})`);
  }
  for (const src of seen) {
    const value = src.trim();
    if (!value || byValue.has(value)) continue;
    byValue.set(value, value);
  }
  return [...byValue.entries()]
    .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: "base" }))
    .map(([value, label]) => ({ value, label }));
}

export function formatReceiptPayload(payload: unknown): string {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}
